// The v2 calendar as the stream builds it (spec §13.6). A port of the
// backend's reference client, tests/v2/headless_client.py: same rules, and
// both are tested against the same recorded fixture
// (__fixtures__/v2-contract.json), so a rule changed on one side fails here.
//
// - events apply in seq order, once: a seq at or below ours is a repeat and is
//   skipped; a seq more than one ahead is a gap, and the caller syncs;
// - unknown event types are ignored (the seq still advances);
// - a row is a session or a race, never both: an upsert of one removes the other.
//
// The conflict badges (the strategy version's blocking findings) can't be
// rebuilt from events: they come from /v2/calendar reads only.
//
// The strategy, its draft, the goal and the coach's working state (M4) are
// strategy.js's: see there.
//
// State is plain data, never mutated: every apply returns a new object, so
// React's useSyncExternalStore sees a change by reference.

import { applyStrategyEvent, strategyFromReads } from './strategy';

export const RACE_HORIZON_DAYS = 366; // app/plan/projection.py: races up to a year past `from`

export class SeqGap extends Error {
  constructor(have, got) {
    super(`seq gap: have ${have}, got ${got}`);
    this.have = have;
    this.got = got;
  }
}

const byId = (rows) => Object.fromEntries((rows || []).map((r) => [String(r.id), r]));
const without = (map, id) => {
  const k = String(id);
  if (!(k in map)) return map;
  const next = { ...map };
  delete next[k];
  return next;
};
const withRow = (map, row) => ({ ...map, [String(row.id)]: row });

/**
 * State from a /v2/calendar read (or a /v2/sync snapshot plus its as_of_seq).
 * `strategy`: the GET /v2/strategy body (null on 404; leave it out when it
 * wasn't read). `prev`: the state this read replaces, for what reads don't
 * carry (the goal, the coach's working state, a strategy not re-read).
 */
export function fromCalendar(cal, { thread = {}, checkins = {}, strategy = undefined, prev = null } = {}) {
  const { strategy: committed, draft } = strategyFromReads(strategy, cal.draft ?? null, prev || {});
  return {
    seq: cal.as_of_seq,
    version: cal.version ?? null,
    sessions: byId(cal.sessions),
    races: byId(cal.races),
    notes: byId(cal.notes),
    training_events: byId(cal.training_events),
    activities: byId(cal.activities),
    conflicts: cal.conflicts || [],
    thread,
    checkins,
    strategy: committed,
    draft,
    goal: prev?.goal ?? null,
    goal_seq: prev?.goal_seq ?? null,
    // The human-coach badge (`coaching.unread`, aithlo-backend#180) and the seq
    // that said so (null: none yet; GET /v2/me's coaching_unread stands).
    coaching_unread: prev?.coaching_unread ?? null,
    coaching_unread_seq: prev?.coaching_unread_seq ?? null,
    work: prev?.work ?? {},
  };
}

/** One stream event onto the state. Throws SeqGap; returns `state` itself for a repeat. */
export function apply(state, event) {
  const { seq } = event;
  if (seq <= state.seq) return state;
  if (seq > state.seq + 1) throw new SeqGap(state.seq, seq);
  const p = event.payload || {};
  let s = { ...state, seq };
  switch (event.type) {
    case 'session.upsert':
      s = { ...s, races: without(s.races, p.id), sessions: withRow(s.sessions, p) };
      break;
    case 'session.remove':
      s = { ...s, sessions: without(s.sessions, p.id) };
      break;
    case 'race.upsert':
      s = { ...s, sessions: without(s.sessions, p.id), races: withRow(s.races, p) };
      break;
    case 'race.remove':
      s = { ...s, races: without(s.races, p.id) };
      break;
    case 'note.upsert':
      s = { ...s, notes: p.deleted_at ? without(s.notes, p.id) : withRow(s.notes, p) };
      break;
    case 'note.remove':
      s = { ...s, notes: without(s.notes, p.id) };
      break;
    case 'training_event.upsert':
      s = { ...s, training_events: withRow(s.training_events, p) };
      break;
    case 'training_event.remove':
      s = { ...s, training_events: without(s.training_events, p.id) };
      break;
    case 'change.applied':
      if (p.version != null) s = { ...s, version: p.version };
      break;
    case 'activity.linked': {
      const act = s.activities[String(p.activity_id)];
      if (act) s = { ...s, activities: withRow(s.activities, { ...act, session_id: p.session_id, matched_by: p.matched_by }) };
      break;
    }
    case 'thread.item':
      s = { ...s, thread: withRow(s.thread, p) };
      break;
    case 'checkin.upsert':
      s = { ...s, checkins: { ...s.checkins, [p.day]: p.feel } };
      break;
    case 'coaching.unread':
      s = { ...s, coaching_unread: Number(p.count) || 0, coaching_unread_seq: seq };
      break;
    default:
      s = applyStrategyEvent(s, event);   // strategy, draft, goal and working state; else a type this app doesn't know yet
      break;
  }
  return s;
}

const day = (iso) => String(iso).slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${day(iso)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const cmp = (a, b) => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
};
const sortBy = (rows, key) => [...rows].sort((x, y) => cmp(key(x), key(y)));
const overlaps = (start, end, lo, hi) => day(start) <= hi && day(end || start) >= lo;

/** The /v2/calendar body for [from, to] (ISO dates), conflicts and as_of_seq aside. */
export function project(state, from, to) {
  const racesTo = [to, addDays(from, RACE_HORIZON_DAYS)].sort().pop();
  const inRange = (d, hi) => day(d) >= from && day(d) <= hi;
  return {
    version: state.version,
    sessions: sortBy(Object.values(state.sessions).filter((x) => inRange(x.scheduled_date, to)), (x) => [x.scheduled_date, x.id]),
    races: sortBy(Object.values(state.races).filter((x) => inRange(x.date, racesTo)), (x) => [x.date, x.id]),
    notes: sortBy(Object.values(state.notes).filter((x) => overlaps(x.start_date, x.end_date, from, to)), (x) => [x.start_date, x.id]),
    training_events: sortBy(Object.values(state.training_events).filter((x) => overlaps(x.starts_at, x.ends_at, from, to)),
      (x) => [x.starts_at, x.id]),
    activities: sortBy(Object.values(state.activities), (x) => [String(x.start_local), x.id]),
  };
}

/** A /v2/calendar body in project()'s shape and order. */
export function comparable(cal) {
  return {
    version: cal.version,
    sessions: sortBy(cal.sessions, (x) => [x.scheduled_date, x.id]),
    races: sortBy(cal.races, (x) => [x.date, x.id]),
    notes: sortBy(cal.notes, (x) => [x.start_date, x.id]),
    training_events: sortBy(cal.training_events, (x) => [x.starts_at, x.id]),
    activities: sortBy(cal.activities, (x) => [String(x.start_local), x.id]),
  };
}

/** The thread, oldest first: items keep their place (their first seq) when re-sent with a new state. */
export function threadItems(state) {
  return sortBy(Object.values(state.thread), (x) => [x.seq, x.id]);
}
